import { useAppDispatch, useAppSelector } from "app.hook";
import { FormikErrors, FormikProps, useFormik } from "formik";
import React, { FC, useEffect, useState } from "react";
import { shallowEqual } from "react-redux";
import { Link } from "react-router-dom";
import { MainContainer } from "shared/components/layout/style/layout-internal-style";
import { ACCOUNT_CHANGE_PASSWORD_ROUTE } from "shared/constants/routes";
import { formatPhone } from "shared/plugins/string-helper";
import { getProfileAPI } from "states/account/account.api";
import {
  getCurrentCustomerInfoAPI,
  updatedCustomerInfoAPI,
} from "states/customer/customer.api";
import {
  ICustomerModel,
  IUpdatedCustomerModel,
} from "states/customer/customer.models";
import {
  fetchCustomer,
  selectCurrentCustomer,
} from "states/customer/customer.slice";
import {
  setActiveMenu,
  setLoading,
  setSignOut,
  showError,
  showSuccess,
} from "states/global-value/global-value.slice";
import { IconEmail, IconLock, IconPhone } from "themes/img";
import { DefaultButton, PrimaryButton } from "themes/style-components/button";
import {
  FlexAuto,
  FlexContainer,
  FlexLeftContainer,
} from "themes/style-components/flex";
import {
  FormControl,
  FormError,
  FormGroup,
  FormLabel,
} from "themes/style-components/form-group";
import { LinkStyle } from "themes/style-components/link";
import { H5 } from "themes/style-components/typography";
import {
  FormUserInfo,
  ItemInfoContainer,
  ItemUserInfoContainer,
  UserDateContainer,
  UserInfoContainer,
} from "./style/user-info-style";

const UserInfoPage: FC = (props) => {
  const currentCustomer = useAppSelector(selectCurrentCustomer, shallowEqual);
  const dispatch = useAppDispatch();
  const formik: FormikProps<IUpdatedCustomerModel> =
    useFormik<IUpdatedCustomerModel>({
      initialValues: {
        firstName: currentCustomer.firstName,
        lastName: currentCustomer?.lastName,
        dayOfBirth: currentCustomer?.dayOfBirth,
        monthOfBirth: currentCustomer?.monthOfBirth,
        yearOfBirth: currentCustomer?.yearOfBirth,
      },
      validateOnChange: true,
      validate: (values) => {
        let errors: FormikErrors<IUpdatedCustomerModel> = {};
        if (values.firstName === "") {
          errors.firstName = "Please enter first name";
        }
        if (values.lastName === "") {
          errors.lastName = "Please enter last name";
        }

        return errors;
      },
      onSubmit: async (values) => {
        dispatch(setLoading(true));
        try {
          await updatedCustomerInfoAPI(values);
          dispatch(fetchCustomer());
          dispatch(setLoading(false));
          dispatch(showSuccess("Updated successfully"));
        } catch (err) {
          dispatch(setLoading(false));
          dispatch(showError(err?.response?.data?.error?.message));
        }
      },
    });
  const handleRenderDay = () => {
    let option = [];
    for (let i = 1; i <= 31; i++) {
      option.push(<option value={i}>{i}</option>);
    }
    return option;
  };
  const handleRenderMonth = () => {
    let option = [];
    for (let i = 1; i <= 12; i++) {
      option.push(<option value={i}>{i}</option>);
    }
    return option;
  };
  const handleRenderYear = () => {
    let option = [];
    for (let i = 1900; i <= 2100; i++) {
      option.push(<option value={i}>{i}</option>);
    }
    return option;
  };
  useEffect(() => {
    formik.setValues({
      firstName: currentCustomer.firstName,
      lastName: currentCustomer?.lastName,
      dayOfBirth: currentCustomer?.dayOfBirth,
      monthOfBirth: currentCustomer?.monthOfBirth,
      yearOfBirth: currentCustomer?.yearOfBirth,
    } as IUpdatedCustomerModel);
  }, [currentCustomer]);
  useEffect(() => {
    document.title = "User Info";
    dispatch(fetchCustomer());
    dispatch(setActiveMenu("user"));
  }, []);
  return (
    <MainContainer>
      <FlexContainer>
        <FormUserInfo onSubmit={formik.handleSubmit}>
          <H5 className="mb-8">User Information</H5>
          <FormGroup className="mb-4">
            <FormLabel htmlFor="firstName">First Name</FormLabel>
            <FlexAuto>
              <FormControl>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  placeholder="First Name"
                  aria-placeholder="Please enter First Name"
                  onChange={formik.handleChange}
                  value={formik.values.firstName}
                />
              </FormControl>
              {formik.errors.firstName && (
                <FormError>{formik.errors.firstName}</FormError>
              )}
            </FlexAuto>
          </FormGroup>
          <FormGroup className="mb-4">
            <FormLabel htmlFor="lastName">Last Name</FormLabel>
            <FlexAuto>
              <FormControl>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  placeholder="Last Name"
                   aria-placeholder="Please enter Last Name"
                  onChange={formik.handleChange}
                  value={formik.values.lastName}
                />
              </FormControl>
              {formik.errors.lastName && (
                <FormError>{formik.errors.lastName}</FormError>
              )}
            </FlexAuto>
          </FormGroup>
          <FormGroup className="mb-4">
            <FormLabel htmlFor="birthday" className="block">
              Birthday
            </FormLabel>
            <FlexAuto>
              <UserDateContainer>
                <FormControl>
                  <select
                    name="dayOfBirth"
                    onChange={formik.handleChange}
                    value={formik.values.dayOfBirth}
                    aria-selected="true"
                     aria-placeholder="Please select a day of birth"
                  >
                    <option value="">Day</option>
                    {handleRenderDay()}
                  </select>
                </FormControl>
                <FormControl>
                  <select
                    name="monthOfBirth"
                    onChange={formik.handleChange}
                    value={formik.values.monthOfBirth}
                    aria-selected="true"
                    aria-placeholder="Please select a month of birth"
                  >
                    <option value="">Month</option>
                    {handleRenderMonth()}
                  </select>
                </FormControl>
                <FormControl>
                  <select
                    name="yearOfBirth"
                    onChange={formik.handleChange}
                    value={formik.values.yearOfBirth}
                    aria-selected="true"
                    aria-placeholder="Please select a year of birth"
                  >
                    <option value="">Year</option>
                    {handleRenderYear()}
                  </select>
                </FormControl>
              </UserDateContainer>
            </FlexAuto>
          </FormGroup>
          <FormGroup className="mb-4">
            <FormLabel htmlFor="button"></FormLabel>
            <FlexAuto>
              <PrimaryButton className="inline-block mt-4" type="submit">
                Save
              </PrimaryButton>
            </FlexAuto>
          </FormGroup>
        </FormUserInfo>
        <UserInfoContainer>
          <H5 className="mb-4">Phone and Email</H5>
          <ItemUserInfoContainer className="mb-2">
            <ItemInfoContainer>
              <IconPhone />
              <div className="info">
                <p>Phone:</p>
                <p>
                  {currentCustomer?.phoneNumber
                    ? formatPhone(currentCustomer?.phoneNumber)
                    : ""}
                </p>
              </div>
            </ItemInfoContainer>
          </ItemUserInfoContainer>
          <ItemUserInfoContainer className="mb-2">
            <ItemInfoContainer>
              <IconEmail />
              <div className="info">
                <p>Email:</p>
                <p>{currentCustomer?.email}</p>
              </div>
            </ItemInfoContainer>
          </ItemUserInfoContainer>
          <H5 className="mb-4 mt-2">Security</H5>
          <ItemUserInfoContainer>
            <ItemInfoContainer>
              <IconLock />
              <div className="info">
                <p>Change Password:</p>
              </div>
            </ItemInfoContainer>
            <Link to={ACCOUNT_CHANGE_PASSWORD_ROUTE}>
              <DefaultButton>Update</DefaultButton>
            </Link>
          </ItemUserInfoContainer>
        </UserInfoContainer>
      </FlexContainer>
    </MainContainer>
  );
};

export default UserInfoPage;
